/**
 * Meter grouping: how a time signature's beats group into strong and weak
 * pulses. Song playback picks its accompaniment pattern from this, and the
 * count-in decides which beats to click.
 *
 * Units are the time signature's denominator (an eighth in 6/8, a half in
 * 2/2). `unitQuarters` converts them to quarter notes, the unit the song
 * tempo is stored in.
 */
/** The conventional groupings for odd and irregular meters. */
const GROUPINGS = {
    5: [3, 2],
    7: [2, 2, 3],
    8: [3, 3, 2],
    10: [3, 3, 2, 2],
    11: [3, 3, 3, 2],
};
const SIMPLE_FEELS = {
    1: 'single',
    2: 'duple',
    3: 'triple',
    4: 'quadruple',
};
function fallbackGroups(numerator) {
    if (numerator % 3 === 0) {
        return Array(numerator / 3).fill(3);
    }
    if (numerator % 2 === 0) {
        return Array(numerator / 2).fill(2);
    }
    // Odd: 2s, with the last group a 3.
    return [...Array((numerator - 3) / 2).fill(2), 3];
}
/** How a time signature's beats group. Never throws on a valid signature. */
export function getMeterInfo(timeSignature) {
    const { numerator, denominator } = timeSignature;
    const unitQuarters = 4 / denominator;
    if (denominator >= 8 && numerator >= 6 && numerator % 3 === 0) {
        return { feel: 'compound', groups: Array(numerator / 3).fill(3), unitQuarters };
    }
    const simple = SIMPLE_FEELS[numerator];
    if (simple) {
        return { feel: simple, groups: Array(numerator).fill(1), unitQuarters };
    }
    return { feel: 'grouped', groups: GROUPINGS[numerator] ?? fallbackGroups(numerator), unitQuarters };
}
/** Bar length in quarter notes: 4/4 → 4, 6/8 → 3, 2/2 → 4. */
export function getBarQuarters(timeSignature) {
    return (timeSignature.numerator * 4) / timeSignature.denominator;
}
